// driverApi.js - API utilities for driver operations

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for driver API');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.body ? errorData.body.error : 'Unknown error or no error message from API'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_driver',
        `Driver API (Live Rides) error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[driverApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_driver',
      `Driver API (Live Rides) network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[driverApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * API utility functions for Driver Management.
 */
export const driverApi = {
  /**
   * Fetches driver schedules by date.
   * GET /driver/schedule?date={date}
   * @param {string} date - The date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of driver schedule objects
   * @throws {Error} - Throws an error if the API request fails or date format is invalid
   */
  fetchDriverSchedulesByDate: async (date) => {
    console.log("Debug: Fetching driver schedules for date:", date);

    // Validate date parameter
    if (!date || date.trim() === '') {
      console.error("Error: Empty date received in API function. Returning empty array.");
      return [];
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) {
      throw new Error('Invalid date format. Expected YYYY-MM-DD');
    }

    try {
      const response = await makeApiRequest(`/driver/schedule?date=${date}`);
      const responseData = await response.json();

      console.log("API Response:", response.status, "Body:", responseData);

      // Check if response contains data array
      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        return responseData.data;
      } else {
        console.warn("Warning: Response data format is unexpected. Returning empty array.");
        return [];
      }
    } catch (error) {
      // Handle specific error cases based on status codes
      if (error.status === 400) {
        throw new Error('Missing or invalid date parameter.');
      } else if (error.status === 500) {
        throw new Error('Internal server error while fetching driver schedules.');
      } else {
        console.error("Error: Failed to fetch driver schedules. Exception:", error);
        throw new Error(`Error fetching driver schedules: ${error.message}`);
      }
    }
  },

  /**
   * Fetches all driver schedules (if such an endpoint exists).
   * GET /driver/schedule
   * @returns {Promise<Array>} Array of all driver schedule objects
   */
  fetchAllDriverSchedules: async () => {
    try {
      const response = await makeApiRequest('/driver/schedule');
      const responseData = await response.json();

      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        return responseData.data;
      } else {
        console.warn("Warning: Response data format is unexpected. Returning empty array.");
        return [];
      }
    } catch (error) {
      console.error("Error: Failed to fetch all driver schedules. Exception:", error);
      throw new Error(`Error fetching all driver schedules: ${error.message}`);
    }
  },

  /**
   * Fetches driver schedules for a date range.
   * GET /driver/schedule?start_date={startDate}&end_date={endDate}
   * @param {string} startDate - The start date in YYYY-MM-DD format
   * @param {string} endDate - The end date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of driver schedule objects
   */
  fetchDriverSchedulesByDateRange: async (startDate, endDate) => {
    // Validate date parameters
    if (!startDate || !endDate) {
      throw new Error('Both startDate and endDate are required');
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(startDate) || !dateRegex.test(endDate)) {
      throw new Error('Invalid date format. Expected YYYY-MM-DD for both dates');
    }

    try {
      const response = await makeApiRequest(`/driver/schedule?start_date=${startDate}&end_date=${endDate}`);
      const responseData = await response.json();

      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        return responseData.data;
      } else {
        console.warn("Warning: Response data format is unexpected. Returning empty array.");
        return [];
      }
    } catch (error) {
      console.error("Error: Failed to fetch driver schedules by date range. Exception:", error);
      throw new Error(`Error fetching driver schedules by date range: ${error.message}`);
    }
  },

  /**
   * Gets driver status for a specific ride and user.
   * GET /driver/status?ride_id={rideId}&user_id={userId}
   * @param {string} rideId - The ID of the ride
   * @param {string} userId - The ID of the user
   * @returns {Promise<Object>} Driver status object
   * @throws {Error} - Throws an error if the API request fails or parameters are invalid
   */
  getDriverStatus: async (rideId, userId) => {
    console.log("Debug: Fetching driver status for rideId:", rideId, "userId:", userId);

    // Validate required parameters
    if (!rideId || rideId.trim() === '') {
      throw new Error('rideId is required and cannot be empty');
    }

    if (!userId || userId.trim() === '') {
      throw new Error('userId is required and cannot be empty');
    }

    try {
      const response = await makeApiRequest(`/driver/status?ride_id=${rideId}&user_id=${userId}`);
      const responseData = await response.json();

      console.log("API Response:", response.status, "Body:", responseData);

      return responseData;
    } catch (error) {
      console.error("Error: Failed to fetch driver status. Exception:", error);
      throw new Error(`Failed to fetch driver status: ${error.message}`);
    }
  },

  /**
   * Fetches all drivers.
   * GET /driver/user/getall
   * @returns {Promise<Array>} Array of driver objects
   * @throws {Error} - Throws an error if the API request fails
   */
  fetchAllDrivers: async () => {
    try {
      const response = await makeApiRequest('/driver/user/getall');
      const responseData = await response.json();

      console.log("API Response:", response.status, "Body:", responseData);

      // Check if response contains data array or is directly an array
      if (Array.isArray(responseData)) {
        return responseData;
      } else if (responseData && Array.isArray(responseData.data)) {
        return responseData.data;
      } else {
        console.warn("Warning: Response data format is unexpected. Returning empty array.");
        return [];
      }
    } catch (error) {
      // Handle specific error cases based on status codes
      if (error.status === 404) {
        throw new Error('No drivers found for the given tenant ID.');
      } else if (error.status === 400) {
        throw new Error('Missing tenant ID.');
      } else {
        console.error("Error: Failed to fetch all drivers. Exception:", error);
        throw new Error(`Failed to fetch drivers: ${error.status || 'Unknown'} - ${error.message}`);
      }
    }
  },

  /**
   * Deletes a driver by employee ID.
   * DELETE /driver/user/delete?employee_id={employeeId}
   * @param {string} employeeId - The employee ID of the driver to delete
   * @returns {Promise<Object>} Response from the delete operation
   * @throws {Error} - Throws an error if the API request fails
   */
  deleteDriver: async (employeeId) => {
    // Validate required parameters
    if (!employeeId || employeeId.trim() === '') {
      throw new Error('Employee ID is required and cannot be empty');
    }

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const url = `${API_BASE_URL}/driver/user/delete?employee_id=${encodeURIComponent(employeeId)}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log("Delete driver API Response:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = `Failed to delete driver: ${response.status} ${response.statusText}`;
        console.error(errorMessage, errorData);
        
        let error;
        if (response.status === 400) {
          error = new Error('Employee ID and Tenant ID are required.');
        } else if (response.status === 207) {
          // Multi-status response: Some deletion steps failed
          const errors = errorData.errors?.join(', ') || 'Unknown error';
          error = new Error(`Partial deletion: ${errors}`);
        } else {
          error = new Error(`Failed to delete driver: ${response.status} - ${errorData.message || errorData.body || 'Unknown error'}`);
        }
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = '/driver/user/delete';
        error.responseData = errorData;
        
        // Log API error to logging service
        loggingService.logError(
          'api_error_delete_driver',
          `Delete Driver API error: ${response.status} ${response.statusText} for deleteDriver`,
          error,
          {
            endpoint: '/driver/user/delete',
            method: 'DELETE',
            status: response.status,
            statusText: response.statusText,
            employeeId,
            responseData: errorData,
          }
        ).catch(logError => {
          console.warn('[driverApi] Failed to log error:', logError);
        });
        
        throw error;
      }

      return await response.json().catch(() => ({}));
    } catch (error) {
      console.error("Error: Failed to delete driver. Exception:", error);
      
      // Log network/fetch errors to logging service
      if (!error.status) {
        loggingService.logError(
          'api_error_delete_driver',
          `Delete Driver API network/fetch error for deleteDriver`,
          error,
          {
            endpoint: '/driver/user/delete',
            method: 'DELETE',
            employeeId,
            errorType: error?.name || 'NetworkError',
          }
        ).catch(logError => {
          console.warn('[driverApi] Failed to log error:', logError);
        });
      }
      
      throw error;
    }
  },

  /**
   * Fetches driver location history for a specific ride and user.
   * GET /driver/location?ride_id={rideId}&user_id={userId}
   * @param {string} rideId - The ID of the ride
   * @param {string} userId - The ID of the user/driver
   * @returns {Promise<Object>} Object containing locations array with latitude, longitude, timestamp
   * @throws {Error} - Throws an error if the API request fails or parameters are invalid
   */
  fetchDriverLocation: async (rideId, userId) => {
    console.log("🚀 Debug: Fetching driver location for Ride ID:", rideId, "User ID:", userId);

    // Validate required parameters
    if (!rideId || rideId.trim() === '') {
      throw new Error('rideId is required and cannot be empty');
    }

    if (!userId || userId.trim() === '') {
      throw new Error('userId is required and cannot be empty');
    }

    try {
      const response = await makeApiRequest(`/driver/location?ride_id=${rideId}&user_id=${userId}`);
      const responseData = await response.json();

      console.log("📩 Debug: Driver location API Response:", response.status, "Body:", responseData);

      return responseData;
    } catch (error) {
      console.error("❌ Error: Failed to fetch driver location. Exception:", error);
      throw new Error(`Failed to fetch driver location: ${error.message}`);
    }
  },

  /**
   * Gets driver stats (speed and battery) for a specific ride and user.
   * GET /driver/stats?ride_id={rideId}&user_id={userId}
   * @param {string} rideId - The ID of the ride
   * @param {string} userId - The ID of the user/driver
   * @returns {Promise<Object>} Driver stats object with average_speed_kmh_last_minute and latest_battery
   * @throws {Error} - Throws an error if the API request fails or parameters are invalid
   */
  getDriverStats: async (rideId, userId) => {
    console.log("🔍 Debug: Fetching driver stats for rideId:", rideId, "userId:", userId);

    // Validate required parameters
    if (!rideId || rideId.trim() === '') {
      throw new Error('rideId is required and cannot be empty');
    }

    if (!userId || userId.trim() === '') {
      throw new Error('userId is required and cannot be empty');
    }

    try {
      const response = await makeApiRequest(`/driver/status?ride_id=${rideId}&user_id=${userId}`);
      const responseData = await response.json();

      console.log("📊 Debug: Driver stats API Response:", response.status, "Body:", responseData);

      return responseData;
    } catch (error) {
      console.error("❌ Error: Failed to fetch driver stats. Exception:", error);
      throw new Error(`Failed to fetch driver stats: ${error.message}`);
    }
  },

  /**
   * Fetches driver details by user ID.
   * GET /driver/user?user_id={userId}
   * @param {string} userId - The ID of the user/driver
   * @returns {Promise<Object>} Driver details object containing user information
   * @throws {Error} - Throws an error if the API request fails or parameters are invalid
   */
  fetchDriverDetails: async (userId) => {
    console.log("🔍 Debug: Fetching driver details for userId:", userId);

    // Validate required parameters
    if (!userId || userId.trim() === '') {
      throw new Error('userId is required and cannot be empty');
    }

    try {
      const response = await makeApiRequest(`/driver/user?user_id=${encodeURIComponent(userId)}`);
      const responseData = await response.json();

      console.log("📩 Debug: Driver details API Response:", response.status, "Body:", responseData);

      return responseData;
    } catch (error) {
      // Handle specific error cases based on status codes
      if (error.status === 404) {
        throw new Error('Driver with ID not found.');
      } else if (error.status === 400) {
        throw new Error('User ID and Tenant ID are required.');
      } else {
        console.error("❌ Error: Failed to fetch driver details. Exception:", error);
        throw new Error(`Failed to fetch driver details: ${error.status || 'Unknown'} - ${error.message}`);
      }
    }
  },

  /**
   * Fetches future driver schedules.
   * GET /driver/schedule/get-future-schedules
   * @returns {Promise<Array>} Array of future driver schedule objects
   * @throws {Error} - Throws an error if the API request fails or authentication token is missing
   */
  fetchFutureDriverSchedules: async () => {
    console.log("Debug: Fetching future driver schedules");

    try {
      const response = await makeApiRequest('/driver/schedule/get-future-schedules');
      const responseData = await response.json();

      console.log("API Response:", response.status, "Body:", responseData);

      // Check if response is directly an array or wrapped in a data property
      if (Array.isArray(responseData)) {
        return responseData;
      } else if (responseData && Array.isArray(responseData.data)) {
        return responseData.data;
      } else {
        console.warn("Warning: Response data format is unexpected. Returning empty array.");
        return [];
      }
    } catch (error) {
      // Handle specific error cases based on status codes
      if (error.status === 400) {
        throw new Error('Missing required identity claims.');
      } else {
        console.error("Error: Failed to fetch future driver schedules. Exception:", error);
        throw new Error(`Failed to fetch future schedules: ${error.status || 'Unknown'} - ${error.message}`);
      }
    }
  },

  /**
   * Fetches available drivers for a given date and time range.
   * GET /driver/user/get-available?from_date={fromDate}&from_time={fromTime}&to_date={toDate}&to_time={toTime}
   * @param {string} fromDate - The start date in DD-MM-YYYY format
   * @param {string} fromTime - The start time in hh:mm a format (12-hour)
   * @param {string} toDate - The end date in DD-MM-YYYY format
   * @param {string} toTime - The end time in hh:mm a format (12-hour)
   * @returns {Promise<Array>} Array of available driver objects
   * @throws {Error} - Throws an error if the API request fails
   */
  fetchAvailableDrivers: async ({ fromDate, fromTime, toDate, toTime }) => {
    if (!fromDate || !fromTime || !toDate || !toTime) {
      throw new Error('All date and time parameters are required');
    }

    try {
      const endpoint = `/driver/user/get-available?from_date=${encodeURIComponent(fromDate)}&from_time=${encodeURIComponent(fromTime)}&to_date=${encodeURIComponent(toDate)}&to_time=${encodeURIComponent(toTime)}`;
      const response = await makeApiRequest(endpoint);
      const responseData = await response.json();

      console.log("API Response for available drivers:", response.status, "Body:", responseData);

      if (responseData && responseData.drivers && Array.isArray(responseData.drivers)) {
        return responseData.drivers;
      } else {
        console.warn("Warning: Response data format is unexpected. Returning empty array.");
        return [];
      }
    } catch (error) {
      console.error("Error: Failed to fetch available drivers. Exception:", error);
      if (error.status === 400) {
        throw new Error('Invalid date or time format.');
      } else {
        throw new Error(`Failed to fetch available drivers: ${error.status || 'Unknown'} - ${error.message}`);
      }
    }
  },

  /**
   * Fetches available vehicles for given sites and date/time range.
   * GET /driver/vehicle/get-available?from_site_id={fromSiteId}&to_site_id={toSiteId}&from_date={fromDate}&from_time={fromTime}&to_date={toDate}&to_time={toTime}
   * @param {string} fromSiteId - The ID of the source site
   * @param {string} toSiteId - The ID of the destination site
   * @param {string} fromDate - The start date in DD-MM-YYYY format
   * @param {string} fromTime - The start time in hh:mm a format (12-hour)
   * @param {string} toDate - The end date in DD-MM-YYYY format
   * @param {string} toTime - The end time in hh:mm a format (12-hour)
   * @returns {Promise<Array>} Array of available vehicle objects
   * @throws {Error} - Throws an error if the API request fails
   */
  fetchAvailableVehicles: async ({ fromSiteId, toSiteId, fromDate, fromTime, toDate, toTime }) => {
    if (!fromSiteId || !toSiteId || !fromDate || !fromTime || !toDate || !toTime) {
      throw new Error('All parameters are required');
    }

    try {
      const endpoint = `/driver/vehicle/get-available?from_site_id=${encodeURIComponent(fromSiteId)}&to_site_id=${encodeURIComponent(toSiteId)}&from_date=${encodeURIComponent(fromDate)}&from_time=${encodeURIComponent(fromTime)}&to_date=${encodeURIComponent(toDate)}&to_time=${encodeURIComponent(toTime)}`;
      const response = await makeApiRequest(endpoint);
      const responseData = await response.json();

      console.log("API Response for available vehicles:", response.status, "Body:", responseData);

      // Combine vehicles from both 'from_site_vehicles' and 'to_site_vehicles'
      const vehicles = [];
      if (responseData.from_site_vehicles && Array.isArray(responseData.from_site_vehicles)) {
        vehicles.push(...responseData.from_site_vehicles);
      }
      if (responseData.to_site_vehicles && Array.isArray(responseData.to_site_vehicles)) {
        vehicles.push(...responseData.to_site_vehicles);
      }

      if (vehicles.length === 0) {
        console.warn("Warning: No vehicles available in the response.");
        return [];
      }

      return vehicles;
    } catch (error) {
      console.error("Error: Failed to fetch available vehicles. Exception:", error);
      if (error.status === 400) {
        throw new Error('Invalid site ID or date/time format.');
      } else {
        throw new Error(`Failed to fetch available vehicles: ${error.status || 'Unknown'} - ${error.message}`);
      }
    }
  },

  /**
   * Creates a driver schedule (ride schedule).
   * POST /driver/schedule/create
   * @param {Object} scheduleData - The schedule data object
   * @param {string} scheduleData.from_site_id - Source site ID
   * @param {string} scheduleData.to_site_id - Destination site ID
   * @param {string} scheduleData.from_date - Start date in DD-MM-YYYY format
   * @param {string} scheduleData.from_time - Start time in hh:mm a format (12-hour)
   * @param {string} scheduleData.to_date - End date in DD-MM-YYYY format
   * @param {string} scheduleData.to_time - End time in hh:mm a format (12-hour)
   * @param {string} scheduleData.average_speed - Average speed in km/h
   * @param {Array} scheduleData.halts - Array of halt objects with address, coordinates, zoom
   * @param {string} scheduleData.from_supervisor_id - Source supervisor user ID
   * @param {string} scheduleData.to_supervisor_id - Destination supervisor user ID
   * @param {Array} scheduleData.assigned_trucks - Array of truck assignment objects
   * @returns {Promise<string>} Success message
   * @throws {Error} - Throws an error if the API request fails
   */
  createDriverSchedule: async (scheduleData) => {
    // Validate required fields
    if (!scheduleData.from_site_id || !scheduleData.to_site_id) {
      throw new Error('from_site_id and to_site_id are required');
    }

    // Process halts to support both legacy string format and enhanced object format
    const processedHalts = [];
    if (scheduleData.halts && Array.isArray(scheduleData.halts)) {
      scheduleData.halts.forEach(halt => {
        if (typeof halt === 'string') {
          // Legacy format - convert to enhanced format
          processedHalts.push({
            address: halt,
            coordinates: null,
            zoom: '15.0'
          });
        } else if (typeof halt === 'object' && halt !== null) {
          // Enhanced format - validate and process
          const haltData = {
            address: halt.address?.toString() || '',
            zoom: halt.zoom?.toString() || '15.0',
          };

          // Add coordinates if present
          if (halt.coordinates && typeof halt.coordinates === 'object') {
            const coords = halt.coordinates;
            if (coords.latitude != null && coords.longitude != null) {
              haltData.coordinates = {
                latitude: coords.latitude.toString(),
                longitude: coords.longitude.toString(),
              };
            }
          }

          processedHalts.push(haltData);
        }
      });
    }

    // Process assigned trucks - handle both nested map format and direct ID format
    const processedTrucks = [];
    if (scheduleData.assigned_trucks && Array.isArray(scheduleData.assigned_trucks)) {
      scheduleData.assigned_trucks.forEach(truck => {
        // Process vehicle and driver values, which may be nested maps
        const vehicleId = truck.vehicle && typeof truck.vehicle === 'object' 
          ? truck.vehicle.id 
          : truck.vehicle;
        const driverId = truck.driver && typeof truck.driver === 'object'
          ? truck.driver.id
          : truck.driver;

        // Check for valid IDs
        if (!vehicleId || !driverId) {
          console.warn("⚠️ Warning: Missing vehicle or driver ID in truck:", truck);
          return; // Skip invalid trucks
        }

        processedTrucks.push({
          vehicle: vehicleId,
          driver: driverId,
          docking_station: truck.docking_station,
          material: truck.material,
        });
      });
    }

    // Build request body
    const body = {
      from_site_id: scheduleData.from_site_id,
      to_site_id: scheduleData.to_site_id,
      from_date: scheduleData.from_date,
      from_time: scheduleData.from_time,
      to_date: scheduleData.to_date,
      to_time: scheduleData.to_time,
      average_speed: scheduleData.average_speed ? scheduleData.average_speed.toString() : null,
      halts: processedHalts,
      cargo_picture: scheduleData.cargo_picture || false,
      parking_site_id: scheduleData.parking_site_id || null,
      from_supervisor_id: scheduleData.from_supervisor_id || null,
      to_supervisor_id: scheduleData.to_supervisor_id || null,
      assigned_trucks: processedTrucks,
    };

    // Remove any null values from the payload
    Object.keys(body).forEach(key => {
      if (body[key] === null || body[key] === undefined) {
        delete body[key];
      }
    });

    try {
      console.log("🚀 Sending request to API: /driver/schedule/create");
      console.log("📦 Request Payload:", JSON.stringify(body, null, 2));

      const response = await makeApiRequest('/driver/schedule/create', 'POST', body);
      const responseData = await response.json();

      console.log("📡 Response Status Code:", response.status);
      console.log("📡 Response Body:", responseData);

      if (response.status === 200) {
        return responseData.message || 'Schedule created successfully';
      } else {
        const errorMessage = responseData.error || 'Unknown error';
        throw new Error(`Failed to create schedule: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Error: Failed to create driver schedule. Exception:", error);
      
      if (error.status === 400) {
        const errorMessage = error.responseData?.error || 'Invalid request';
        throw new Error(`Validation error: ${errorMessage}`);
      } else if (error.status === 403) {
        throw new Error('Permission denied. Please check your access rights.');
      } else if (error.status === 500) {
        const errorMessage = error.responseData?.error || 'Server error';
        throw new Error(`Server error: ${errorMessage}`);
      } else {
        throw new Error(`Unexpected error: ${error.status || 'Unknown'} - ${error.message}`);
      }
    }
  },

  /**
   * Updates a driver schedule (ride schedule).
   * PUT /driver/schedule/update
   * @param {Object} updateData - The update data object
   * @param {string} updateData.ride_id - The ID of the ride to update (required)
   * @param {string} [updateData.from_site_id] - Source site ID
   * @param {string} [updateData.to_site_id] - Destination site ID
   * @param {string} [updateData.from_date] - Start date in DD-MM-YYYY format
   * @param {string} [updateData.from_time] - Start time in hh:mm a format (12-hour)
   * @param {string} [updateData.to_date] - End date in DD-MM-YYYY format
   * @param {string} [updateData.to_time] - End time in hh:mm a format (12-hour)
   * @param {string} [updateData.average_speed] - Average speed in km/h
   * @param {boolean} [updateData.cargo_picture] - Cargo picture flag
   * @param {string} [updateData.parking_site_id] - Parking site ID
   * @param {Array} [updateData.assigned_trucks] - Array of truck assignment objects (same format as create)
   * @param {string} [updateData.from_supervisor_id] - Source supervisor user ID
   * @param {string} [updateData.to_supervisor_id] - Destination supervisor user ID
   * @returns {Promise<string>} Success message
   * @throws {Error} - Throws an error if the API request fails
   */
  updateDriverSchedule: async (updateData) => {
    // Validate required fields
    if (!updateData.ride_id) {
      throw new Error('ride_id is required');
    }

    // Build request body dynamically (only include non-null values)
    const body = {
      ride_id: updateData.ride_id,
    };

    if (updateData.from_site_id != null) body.from_site_id = updateData.from_site_id;
    if (updateData.to_site_id != null) body.to_site_id = updateData.to_site_id;
    if (updateData.from_date != null) body.from_date = updateData.from_date;
    if (updateData.from_time != null) body.from_time = updateData.from_time;
    if (updateData.to_date != null) body.to_date = updateData.to_date;
    if (updateData.to_time != null) body.to_time = updateData.to_time;
    if (updateData.cargo_picture != null) body.cargo_picture = updateData.cargo_picture;
    if (updateData.parking_site_id != null) body.parking_site_id = updateData.parking_site_id;
    if (updateData.average_speed != null) body.average_speed = updateData.average_speed.toString();
    if (updateData.from_supervisor_id != null) body.from_supervisor_id = updateData.from_supervisor_id;
    if (updateData.to_supervisor_id != null) body.to_supervisor_id = updateData.to_supervisor_id;

    // Process halts to support both legacy string format and enhanced object format
    if (updateData.halts != null && Array.isArray(updateData.halts)) {
      const processedHalts = [];
      updateData.halts.forEach(halt => {
        if (typeof halt === 'string') {
          // Legacy format - convert to enhanced format
          processedHalts.push({
            address: halt,
            coordinates: null,
            zoom: '15.0'
          });
        } else if (typeof halt === 'object' && halt !== null) {
          // Enhanced format - validate and process
          const haltData = {
            address: halt.address?.toString() || '',
            zoom: halt.zoom?.toString() || '15.0',
          };

          // Add coordinates if present
          if (halt.coordinates && typeof halt.coordinates === 'object') {
            const coords = halt.coordinates;
            if (coords.latitude != null && coords.longitude != null) {
              haltData.coordinates = {
                latitude: coords.latitude.toString(),
                longitude: coords.longitude.toString(),
              };
            }
          }

          processedHalts.push(haltData);
        }
      });
      body.halts = processedHalts;
    }

    // Process assigned trucks - same format as create (handle both nested map format and direct ID format)
    if (updateData.assigned_trucks != null && Array.isArray(updateData.assigned_trucks)) {
      const processedTrucks = [];
      updateData.assigned_trucks.forEach(truck => {
        // Process vehicle and driver values, which may be nested maps
        const vehicleId = truck.vehicle && typeof truck.vehicle === 'object' 
          ? truck.vehicle.id 
          : truck.vehicle;
        const driverId = truck.driver && typeof truck.driver === 'object'
          ? truck.driver.id
          : truck.driver;

        // Check for valid IDs
        if (!vehicleId || !driverId) {
          console.warn("⚠️ Warning: Missing vehicle or driver ID in truck:", truck);
          return; // Skip invalid trucks
        }

        processedTrucks.push({
          vehicle: vehicleId,
          driver: driverId,
          docking_station: truck.docking_station && typeof truck.docking_station === 'object'
            ? truck.docking_station.id
            : truck.docking_station,
          material: truck.material,
        });
      });
      body.assigned_trucks = processedTrucks;
    }

    try {
      // console.log("🚀 Sending request to API: /driver/schedule/update");
      console.log("📦 Request Payload:", JSON.stringify(body, null, 2));

      const response = await makeApiRequest('/driver/schedule/update', 'PUT', body);
      const responseData = await response.json();

      console.log("📡 Response Status Code:", response.status);
      console.log("📡 Response Body:", responseData);

      if (response.status === 200) {
        return responseData.message || 'Schedule updated successfully';
      } else {
        const errorMessage = responseData.error || 'Unknown error';
        throw new Error(`Failed to update schedule: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Error: Failed to update driver schedule. Exception:", error);
      
      if (error.status === 400) {
        const errorMessage = error.responseData?.error || 'Invalid request';
        throw new Error(`Validation error: ${errorMessage}`);
      } else if (error.status === 403) {
        throw new Error('Permission denied. Please check your access rights.');
      } else if (error.status === 500) {
        const errorMessage = error.responseData?.error || 'Server error';
        throw new Error(`Server error: ${errorMessage}`);
      } else {
        throw new Error(`Unexpected error: ${error.status || 'Unknown'} - ${error.message}`);
      }
    }
  },

  /**
   * Deletes a driver schedule (ride schedule) and its associated attendance records.
   * DELETE /driver/schedule/delete?ride_id={rideId}
   * @param {string} rideId - The ID of the ride schedule to delete
   * @returns {Promise<Object>} Response from the delete operation
   * @throws {Error} - Throws an error if the API request fails
   */
  deleteDriverSchedule: async (rideId) => {
    // Validate required parameters
    if (!rideId || rideId.trim() === '') {
      throw new Error('Ride ID is required and cannot be empty');
    }

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const url = `${API_BASE_URL}/driver/schedule/delete?ride_id=${encodeURIComponent(rideId)}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log("Delete driver schedule API Response:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = `Failed to delete driver schedule: ${response.status} ${response.statusText}`;
        console.error(errorMessage, errorData);
        
        if (response.status === 400) {
          throw new Error('Missing required query parameter: ride_id');
        } else if (response.status === 500) {
          const errorMsg = errorData.error || errorData.body?.error || 'Internal server error';
          throw new Error(`Error deleting schedule: ${errorMsg}`);
        } else {
          throw new Error(`Failed to delete driver schedule: ${response.status} - ${errorData.message || errorData.body || 'Unknown error'}`);
        }
      }

      const responseData = await response.json();
      console.log("Successfully deleted driver schedule:", responseData);
      return responseData;
    } catch (error) {
      console.error("Error: Failed to delete driver schedule. Exception:", error);
      throw error;
    }
  }
};
